/**
 * Silent Audio Anchor — a hidden HTML5 <audio> element looping a 1-second
 * silent file. The point isn't to make sound; it's to make the OS recognise
 * our PAGE as the active audio source.
 *
 * Why bother (vs. Web Audio API silent loop we already have in keepAlive.js)?
 *   - iOS Safari + Chrome on Android grant **higher background priority** to
 *     pages with an actually-playing <audio> element. Web Audio's silent
 *     OscillatorNode does NOT qualify for this priority bump.
 *   - The Media Session metadata + lock-screen controls only attach when
 *     the system sees an HTML5 media element claiming audio focus.
 *   - When the cross-origin YouTube iframe gets throttled, our anchor stays
 *     audible-state from the OS's POV, which lets the iframe's audio survive
 *     in many cases (especially Android Chrome).
 *
 * Autoplay policy: the first .play() call MUST come from a user gesture
 * (e.g. the DJ tapping the Play button). After that, subsequent .play()/
 * .pause() calls work programmatically.
 */

const SILENT_SRC = '/silent.wav';
let audioEl = null;
let armed = false;

function ensureElement() {
  if (audioEl) return audioEl;
  if (typeof document === 'undefined') return null;
  const el = document.createElement('audio');
  el.src = SILENT_SRC;
  el.loop = true;
  el.preload = 'auto';
  // Keep the volume so low that even if the browser plays a glitch it's
  // inaudible, but >0 so the OS treats us as "playing audio".
  el.volume = 0.0001;
  el.setAttribute('playsinline', 'true');
  el.setAttribute('webkit-playsinline', 'true');
  // Keep it offscreen + non-interactive.
  el.style.position = 'fixed';
  el.style.left = '-9999px';
  el.style.top = '-9999px';
  el.style.width = '1px';
  el.style.height = '1px';
  el.style.opacity = '0';
  el.setAttribute('aria-hidden', 'true');
  el.setAttribute('tabindex', '-1');
  el.id = 'spin-it-up-silent-anchor';
  document.body.appendChild(el);
  audioEl = el;
  return audioEl;
}

/**
 * Arm + start the silent anchor. MUST be called from inside a user gesture
 * (a click/tap handler) for the first time on iOS/Safari; idempotent so
 * subsequent calls are safe from anywhere.
 *
 * Returns a Promise that resolves on success / silently rejects on autoplay
 * block — the caller doesn't need to handle the rejection.
 */
export function armSilentAudio() {
  const el = ensureElement();
  if (!el) return Promise.resolve();
  armed = true;
  const p = el.play();
  if (p && typeof p.catch === 'function') {
    return p.catch((err) => {
      console.warn('[silentAnchor] play() blocked — first call must be from a user gesture:', err?.name || err);
    });
  }
  return Promise.resolve();
}

/**
 * Programmatic play/pause for the anchor — mirrors the YouTube player state
 * so the OS's "is something playing?" indicator stays accurate.
 *
 * Will NO-OP if the anchor hasn't been armed by a user gesture yet (avoids
 * spurious autoplay-block warnings during silent app boot).
 */
export function setSilentAudioPlaying(shouldPlay) {
  const el = audioEl;
  if (!el || !armed) return;
  if (shouldPlay && el.paused) {
    el.play().catch(() => {});
  } else if (!shouldPlay && !el.paused) {
    el.pause();
  }
}

/** Returns true once the anchor has been armed (first user-gesture play). */
export function isSilentAudioArmed() {
  return armed;
}
